'use client';

import { useMemo, type ComponentType, type ReactNode } from 'react';
import { z } from 'zod';
import type {
  PluginChatCardProps,
  PluginChatDockProps,
  PluginChatPickerProps,
  PluginChatRailSectionProps,
  PluginHistoryBranchProps,
  PluginUiRegistration,
} from 'elowen-plugin-ui-kit';
import type { PluginUiListing } from './types';
import { usePluginUiRegistrations, pluginSlotEntries, loadPluginUi } from './pluginUi';
import { usePluginUi } from './queries';
import { interpolate, useTranslation } from './i18n';
import { PluginErrorBoundary, PluginPlaceholder } from '../components/plugin/PluginUiGuards';
import { Button } from '../components/ui/Button';

/** Decode history branch session targets; Subagent omits the continuation flag. */
export function parseBrainSessionTarget(target: string): { sessionId: string; continuable: boolean } | null {
  const parts = target.split(':');
  const [kind, encodedId, flag] = parts;
  if ((parts.length !== 2 && parts.length !== 3) || kind !== 'session' || !encodedId ||
      (flag !== undefined && flag !== '0' && flag !== '1')) return null;
  try {
    return { sessionId: decodeURIComponent(encodedId), continuable: flag === '1' };
  } catch (error) {
    if (error instanceof URIError) return null;
    throw error;
  }
}

type Picker = ComponentType<PluginChatPickerProps>;
type Card = ComponentType<PluginChatCardProps>;

export interface PluginPickerInvocation {
  plugin: string;
  command: string;
  sessionId: string | null;
  argument?: string;
}

export function chatPickerEntries(listing: readonly PluginUiListing[] | undefined): PluginUiListing[] {
  return pluginSlotEntries(listing, 'chatPickers');
}

export function chatCardEntries(listing: readonly PluginUiListing[] | undefined): PluginUiListing[] {
  return pluginSlotEntries(listing, 'chatCards');
}

const railCardClaims = z.record(z.string().min(1), z.array(z.string().min(1)));

/** Explicit card-to-section claims from the same eligible registrations the contribution hosts use.
 *  Consumers still require addressable rows and a rail that owns live work before hiding a card. */
export function useRailTakeovers(): {
  ready: boolean;
  byCard: ReadonlyMap<string, { plugin: string; section: string }[]>;
} {
  const { locale } = useTranslation();
  const listing = usePluginUi(locale);
  const entries = useMemo(() => chatCardEntries(listing.data), [listing.data]);
  const loaded = usePluginUiRegistrations(entries, loadPluginUi);
  const ready = !listing.isPending && loaded.ready;
  return useMemo(() => {
    const byCard = new Map<string, { plugin: string; section: string }[]>();
    if (!ready) return { ready, byCard };

    for (const { entry, registration, compatible } of loaded.registrations) {
      if (!compatible || !registration || !entry.slots.includes('chatRailSections')) continue;
      const claims = railCardClaims.safeParse(registration.chatRailCards);
      if (!claims.success) continue;
      const cards = componentMapOf(registration, 'chatCards', entry.name);
      const sections = componentMapOf(registration, 'chatRailSections', entry.name);
      for (const [section, cardIds] of Object.entries(claims.data)) {
        if (!Object.hasOwn(sections, section)) continue;
        for (const cardId of new Set(cardIds)) {
          if (!Object.hasOwn(cards, cardId)) continue;
          const owners = byCard.get(cardId) ?? [];
          owners.push({ plugin: entry.name, section });
          byCard.set(cardId, owners);
        }
      }
    }
    return { ready, byCard };
  }, [ready, loaded.registrations]);
}

function chatRailSectionEntries(listing: readonly PluginUiListing[] | undefined): PluginUiListing[] {
  return pluginSlotEntries(listing, 'chatRailSections');
}

function chatDockEntries(listing: readonly PluginUiListing[] | undefined): PluginUiListing[] {
  return pluginSlotEntries(listing, 'chatDock');
}

function historyBranchEntries(listing: readonly PluginUiListing[] | undefined): PluginUiListing[] {
  return pluginSlotEntries(listing, 'historyBranches');
}

type RegistrationKey = 'chatPickers' | 'chatCards' | 'chatRailSections' | 'chatDock' | 'historyBranches';

/** The registration's components for one slot, keyed by their id. `chatDock` is one view per plugin, so it is
 *  keyed by the plugin itself. */
function componentMapOf(registration: unknown, key: RegistrationKey, plugin: string): Record<string, unknown> {
  const raw = (registration as PluginUiRegistration | null | undefined)?.[key];
  if (key === 'chatDock') return typeof raw === 'function' ? { [plugin]: raw } : {};
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  return Object.fromEntries(Object.entries(raw).filter(([, value]) => typeof value === 'function'));
}

function usePluginComponents<T>(
  entries: readonly PluginUiListing[],
  key: RegistrationKey,
): { components: Map<string, T>; ready: boolean } {
  const { registrations, ready } = usePluginUiRegistrations(entries, loadPluginUi);
  const components = new Map<string, T>();
  for (const { entry, registration, compatible } of registrations) {
    if (!compatible) continue;
    for (const [id, component] of Object.entries(componentMapOf(registration, key, entry.name))) components.set(id, component as T);
  }
  return { components, ready };
}

/** The picker a plugin registered for one slash command, once the bundle has had its say. `ready` is false
 *  while the bundle loads; after that `Picker` is either the registered component or undefined (the plugin is
 *  off, too new for this host, or has no picker for the command). The host and the notice both read it, so
 *  they can never disagree about whether a picker exists. */
function usePluginChatPicker(invocation: PluginPickerInvocation): { ready: boolean; Picker: Picker | undefined } {
  const { locale } = useTranslation();
  const listing = usePluginUi(locale);
  const entries = useMemo(
    () => chatPickerEntries(listing.data).filter((entry) => entry.name === invocation.plugin),
    [invocation.plugin, listing.data],
  );
  const { components, ready } = usePluginComponents<Picker>(entries, 'chatPickers');
  return { ready, Picker: components.get(invocation.command) };
}

/** Mount a plugin-owned picker. The picker is surface-local in meaning but mounted once by the shared chat
 *  provider, so two chat surfaces never duplicate a modal. A picker that cannot be mounted renders nothing
 *  here: its notice is {@link PluginChatPickerNotice}, which a chat surface draws in place. */
export function PluginChatPickerHost({ invocation, sendAction, closeAction, onCrash }: {
  invocation: PluginPickerInvocation;
  sendAction: PluginChatPickerProps['send'];
  closeAction: () => void;
  /** The picker threw while rendering; the surface's notice takes over, so this host draws nothing. */
  onCrash: (invocation: PluginPickerInvocation) => void;
}) {
  const { ready, Picker } = usePluginChatPicker(invocation);
  if (!ready || !Picker) return null;
  return (
    <PluginErrorBoundary notice="" fallback={null} onFailure={() => onCrash(invocation)}>
      <Picker
        plugin={invocation.plugin}
        command={invocation.command}
        sessionId={invocation.sessionId}
        argument={invocation.argument}
        send={sendAction}
        close={closeAction}
      />
    </PluginErrorBoundary>
  );
}

/** The single notice frame for a picker that cannot be shown, whether it was never registered or threw while
 *  rendering, with the one way out. It is not an overlay: the provider sits beside the app shell, so a
 *  surface renders this inside its own footer, where the reader is looking. Renders nothing while the bundle
 *  loads or when the picker mounted and is healthy. */
export function PluginChatPickerNotice({ invocation, crashed, closeAction }: { invocation: PluginPickerInvocation; crashed: boolean; closeAction: () => void }) {
  const { t } = useTranslation();
  const { ready, Picker } = usePluginChatPicker(invocation);
  if (!crashed && (!ready || Picker)) return null;
  return (
    <PluginPlaceholder
      role="alert"
      text={crashed ? t.pluginUi.crashed : interpolate(t.pluginUi.pickerUnavailable, { command: invocation.command, plugin: invocation.plugin })}
      action={<Button type="button" variant="outline" size="sm" onClick={closeAction}>{t.common.close}</Button>}
    />
  );
}

/** Select a registered renderer by opaque card id. The host keeps unknown and legacy cards generic. */
export function PluginChatCardHost({ card, sessionId, live, open, fallback }: PluginChatCardProps & { fallback: ReactNode }) {
  const { locale, t } = useTranslation();
  const listing = usePluginUi(locale);
  const entries = useMemo(() => chatCardEntries(listing.data), [listing.data]);
  const { components, ready } = usePluginComponents<Card>(entries, 'chatCards');
  const Card = components.get(card.id);

  if (!ready || !Card) return <>{fallback}</>;
  return (
    <PluginErrorBoundary notice={t.pluginUi.crashed}>
      <Card card={card} sessionId={sessionId} live={live} open={open} />
    </PluginErrorBoundary>
  );
}

/** Render one plugin-owned rail section. The host owns the rail geometry and data projection; the bundle
 * owns validation and presentation. A missing or failed contribution is omitted so sibling sections survive. */
export function PluginChatRailSectionHost({ plugin, section, variant, sessionId, data, open, closeMobile }: {
  plugin: string;
  section: string;
  variant: 'expanded' | 'compact';
  sessionId: string | null;
  data: unknown;
  open: (target: string) => void;
  closeMobile?: () => void;
}) {
  const { locale, t } = useTranslation();
  const listing = usePluginUi(locale);
  const entries = useMemo(
    () => chatRailSectionEntries(listing.data).filter((entry) => entry.name === plugin),
    [listing.data, plugin],
  );
  const { components, ready } = usePluginComponents<ComponentType<PluginChatRailSectionProps>>(entries, 'chatRailSections');
  const Section = components.get(section);
  if (!ready || !Section) return null;
  return (
    <PluginErrorBoundary notice={t.pluginUi.crashed}>
      <Section variant={variant} sessionId={sessionId} data={data} open={open} closeMobile={closeMobile} />
    </PluginErrorBoundary>
  );
}

/** Mount every plugin's chat dock view for the conversation on screen. The host hands over only the
 *  conversation's Project and live narration; what to show for it, and whether to show anything, is the
 *  bundle's. A view that fails is dropped alone, so a sibling plugin's view and the transcript survive. */
export function PluginChatDockHost({ project, sessionId, narration, pendingInput }: Omit<PluginChatDockProps, 'plugin'>) {
  const { locale, t } = useTranslation();
  const listing = usePluginUi(locale);
  const entries = useMemo(() => chatDockEntries(listing.data), [listing.data]);
  const { components, ready } = usePluginComponents<ComponentType<PluginChatDockProps>>(entries, 'chatDock');
  if (!ready) return null;
  return (
    <>
      {[...components].map(([plugin, Dock]) => (
        <PluginErrorBoundary key={plugin} notice={t.pluginUi.crashed}>
          <Dock plugin={plugin} project={project} sessionId={sessionId} narration={narration} pendingInput={pendingInput} />
        </PluginErrorBoundary>
      ))}
    </>
  );
}

/** Render one plugin-owned history branch. Core supplies the projected parent and opaque branch items,
 * while the bundle decides how its rows look. The explicit fallback remains a generic host row. */
export function PluginHistoryBranchHost({ plugin, branch, parent, items, expanded, toggle, open, fallback }: {
  plugin: string;
  branch: string;
  parent: PluginHistoryBranchProps['parent'];
  items: readonly unknown[];
  expanded: boolean;
  toggle: () => void;
  open: (target: string) => void;
  fallback: ReactNode;
}) {
  const { locale, t } = useTranslation();
  const listing = usePluginUi(locale);
  const entries = useMemo(
    () => historyBranchEntries(listing.data).filter((entry) => entry.name === plugin),
    [listing.data, plugin],
  );
  const { components, ready } = usePluginComponents<ComponentType<PluginHistoryBranchProps>>(entries, 'historyBranches');
  const Branch = components.get(branch);
  if (!ready || !Branch) return <>{fallback}</>;
  return (
    <PluginErrorBoundary notice={t.pluginUi.crashed} fallback={fallback}>
      <Branch parent={parent} items={items} expanded={expanded} toggle={toggle} open={open} />
    </PluginErrorBoundary>
  );
}
